'use client'

import { useRouter } from 'next/navigation'
import { useMemo, useEffect, useRef } from 'react'
import { ContentWrapper } from '@/General/ContentWrapper'
import { H1 } from '@/General/H1'
import { InfoAlert } from '@/General/InfoAlert'
import { Button } from '@/General/Button'
import { CheckoutSteps } from '../../../checkout/CheckoutSteps'
import { useForm, useWatch } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { RegistrantsCountSelector } from './RegistrantsCountSelector'
import { RegistrationTypeSelector } from './RegistrationTypeSelector'
import { RegisterLoadingState } from './RegisterLoadingState'
import { RegisterErrorState } from './RegisterErrorState'
import { useConventionProductByStub } from './useConventionProductByStub'
import {
  ConventionRegistrationProductQuery,
  ProductByIdDocument,
  ProductByIdQuery,
  ProductByIdQueryVariables,
} from '@/generated/graphql'
import type { BrandCode } from '@/utils/resolveBrandCode'
import { useQueryClient } from '@tanstack/react-query'
import { fetchData } from '@/lib/fetcher'
import {
  trackSelectedConventionRegistrationTicketType,
  trackViewedConventionRegistrationStart,
} from './conventionTracking'
import { formatRegistrationDropdownPrice } from '@/utils/currency'
import { buildConventionRegistrationParams } from './registrationDraft'

interface FormData {
  registrants: number
  registrationOptionId: number | null
}

type ConventionRegistrationProduct =
  ConventionRegistrationProductQuery['ecommProductBySlug']

type RegisterStartPageClientProps = {
  conventionSlug: string
  initialBrandCode: BrandCode
  initialProduct: ConventionRegistrationProduct
  bypassSoldOut: boolean
}

export default function RegisterStartPageClient({
  conventionSlug,
  initialBrandCode,
  initialProduct,
  bypassSoldOut,
}: RegisterStartPageClientProps) {
  const router = useRouter()
  const queryClient = useQueryClient()

  const stub = conventionSlug?.trim() ?? ''
  const { product, brandCode, isPending, isError, currency } =
    useConventionProductByStub(stub, {
      initialBrandCode,
      initialProduct,
    })
  const productId = product?.id

  const registrationOptions = useMemo(() => {
    return product?.options || []
  }, [product?.options])

  const hasOptions = registrationOptions.length > 0
  const requiresOption = registrationOptions.length > 1
  const defaultOptionId = useMemo(() => {
    if (registrationOptions.length === 0) return null
    return registrationOptions[0].id ?? null
  }, [registrationOptions])

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitted },
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      registrants: 1,
      registrationOptionId: null,
    },
    mode: 'onSubmit',
  })

  useEffect(() => {
    if (defaultOptionId != null) {
      setValue('registrationOptionId', defaultOptionId)
    }
  }, [defaultOptionId, setValue])

  const productLevelPrice = useMemo(() => {
    if (registrationOptions.length > 0) return null
    if (product?.displayPrice == null) return null
    const n = Number(product.displayPrice)
    if (!Number.isFinite(n) || n <= 0) return null
    return n
  }, [product, registrationOptions])

  const currencySymbol =
    registrationOptions[0]?.currency?.symbol ??
    currency?.symbol ??
    product?.currencySymbol ??
    '$'
  const displayCurrencySymbol = currencySymbol
  const selectedRegistrationOptionId = useWatch({
    control,
    name: 'registrationOptionId',
  })
  const selectedRegistrants = useWatch({
    control,
    name: 'registrants',
  })
  const viewedStartTrackedRef = useRef(false)
  const isRetreatConvention = useMemo(() => {
    return (
      conventionSlug?.toLowerCase().includes('retreat') ||
      product?.name?.toLowerCase().includes('retreat') ||
      false
    )
  }, [conventionSlug, product?.name])

  const isWaitlistDisabled = useMemo(() => {
    const value = product?.meta?.find(
      m => m.key.toLowerCase() === 'waitinglistoff'
    )?.value
    return value?.toLowerCase() === 'true'
  }, [product?.meta])

  /** Conventions are not warehouse-backed; use `soldout` meta only, not option `inStock`. */
  const isConventionSoldOut = useMemo(() => {
    const soldOutMeta = product?.meta?.find(
      m => m.key.toLowerCase() === 'soldout'
    )?.value
    return soldOutMeta?.toLowerCase() === 'true'
  }, [product?.meta])

  const isRegistrationClosed = isConventionSoldOut && !bypassSoldOut
  const shouldOfferWaitlist = !isWaitlistDisabled && isRegistrationClosed

  const waitlistHref = useMemo(() => {
    if (!productId) return null

    const queryParams = new URLSearchParams({
      conventionProductId: productId.toString(),
    })
    if (selectedRegistrationOptionId) {
      queryParams.set(
        'registrationOptionId',
        selectedRegistrationOptionId.toString()
      )
    }

    return `/magic-conventions/${conventionSlug}/register/waitlist?${queryParams.toString()}`
  }, [conventionSlug, productId, selectedRegistrationOptionId])

  const maxQuantity = useMemo(() => {
    const limits = registrationOptions
      .map(o => o.maxQuantity)
      .filter((v): v is number => typeof v === 'number' && v > 0)
    if (limits.length === 0) return 5
    return Math.min(...limits)
  }, [registrationOptions])

  const availableWorkshopIds = useMemo(() => {
    if (!product?.meta) return []
    return product.meta
      .filter(m => m.key.startsWith('workshop'))
      .map(m => Number(m.value))
      .filter((id): id is number => Number.isFinite(id) && id > 0)
  }, [product])

  // Ensure form value stays valid when maxQuantity is 1 (server-driven).
  useEffect(() => {
    if (maxQuantity === 1) {
      setValue('registrants', 1)
    }
  }, [maxQuantity, setValue])

  useEffect(() => {
    if (isRetreatConvention) {
      setValue('registrants', 1)
    }
  }, [isRetreatConvention, setValue])

  useEffect(() => {
    if (!brandCode || availableWorkshopIds.length === 0) return

    for (const workshopId of availableWorkshopIds) {
      void queryClient.prefetchQuery({
        queryKey: ['workshopProduct', workshopId, brandCode],
        queryFn: () =>
          fetchData<ProductByIdQuery, ProductByIdQueryVariables>(
            ProductByIdDocument,
            {
              id: workshopId,
              brandCode,
            }
          )(),
      })
    }
  }, [availableWorkshopIds, brandCode, queryClient])

  useEffect(() => {
    if (!product?.id) return
    const registrantsCount = isRetreatConvention ? 1 : selectedRegistrants || 1
    const params = buildConventionRegistrationParams({
      conventionProductId: product.id,
      registrationOptionId: selectedRegistrationOptionId ?? null,
      registrantsCount,
      bypassSoldOut,
    })
    router.prefetch(
      `/magic-conventions/${conventionSlug}/register/details?${params.toString()}`
    )
  }, [
    conventionSlug,
    product?.id,
    router,
    bypassSoldOut,
    isRetreatConvention,
    selectedRegistrants,
    selectedRegistrationOptionId,
  ])

  useEffect(() => {
    if (viewedStartTrackedRef.current) return
    if (!product?.id) return

    viewedStartTrackedRef.current = true
    const selectedOption =
      registrationOptions.find(
        option => option.id === selectedRegistrationOptionId
      ) ??
      registrationOptions[0] ??
      null

    trackViewedConventionRegistrationStart({
      conventionSlug,
      conventionProductId: product.id,
      conventionName: product.name,
      registrationOptionId: selectedOption?.id ?? null,
      registrationOptionName: selectedOption?.description ?? null,
      registrantsCount: isRetreatConvention ? 1 : selectedRegistrants || 1,
    })
  }, [
    conventionSlug,
    isRetreatConvention,
    product?.id,
    product?.name,
    registrationOptions,
    selectedRegistrants,
    selectedRegistrationOptionId,
  ])

  useEffect(() => {
    if (!shouldOfferWaitlist || !waitlistHref) return
    router.replace(waitlistHref)
  }, [router, shouldOfferWaitlist, waitlistHref])

  const onSubmit = (data: FormData) => {
    if (!product?.id) return
    const registrantsCount = isRetreatConvention ? 1 : data.registrants

    const queryParams = buildConventionRegistrationParams({
      conventionProductId: product.id,
      registrationOptionId: data.registrationOptionId,
      registrantsCount,
      bypassSoldOut,
    })

    router.push(
      `/magic-conventions/${conventionSlug}/register/details?${queryParams.toString()}`
    )
  }

  if (isPending) {
    return <RegisterLoadingState />
  }

  if (isError || !product) {
    return (
      <RegisterErrorState
        conventionSlug={conventionSlug}
        brandCode={brandCode}
        hasError={isError}
      />
    )
  }

  if (shouldOfferWaitlist) {
    return <RegisterLoadingState />
  }

  if (isRegistrationClosed) {
    return (
      <div>
        <CheckoutSteps current="cart" />
        <ContentWrapper>
          <div className="max-w-3xl mx-auto">
            <InfoAlert
              variant="error"
              title={`${product.name || 'This convention'} is sold out`}
            >
              Registration is closed for this convention.
            </InfoAlert>
          </div>
        </ContentWrapper>
      </div>
    )
  }

  return (
    <div>
      <CheckoutSteps current="cart" />
      <ContentWrapper>
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <H1 className="mb-4">{product.name} Registration</H1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Join us for an unforgettable experience. Select your registration
              details below to get started.
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
            noValidate
          >
            {isSubmitted && (
              <ErrorBox
                errors={
                  Object.values(errors)
                    .map(error => error?.message)
                    .filter(Boolean) as string[]
                }
              />
            )}

            {isRetreatConvention ? (
              <input
                type="hidden"
                value={1}
                {...register('registrants', { valueAsNumber: true })}
              />
            ) : (
              <RegistrantsCountSelector
                register={register}
                errors={errors}
                isSubmitted={isSubmitted}
                maxQuantity={maxQuantity}
              />
            )}

            {productLevelPrice != null && (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-sm">
                <p className="text-lg font-semibold text-gray-900">
                  Registration
                </p>
                <p className="mt-1 text-xl text-gray-800">
                  {displayCurrencySymbol}
                  {formatRegistrationDropdownPrice(productLevelPrice)} per
                  person
                </p>
              </div>
            )}

            {hasOptions && (
              <RegistrationTypeSelector
                register={register}
                errors={errors}
                isSubmitted={isSubmitted}
                registrationOptions={registrationOptions}
                requiresOption={requiresOption}
                defaultOptionId={defaultOptionId}
                selectedOptionId={selectedRegistrationOptionId}
                currencySymbol={displayCurrencySymbol}
                conventionSlug={conventionSlug}
                onOptionSelected={(optionId, optionName) =>
                  trackSelectedConventionRegistrationTicketType({
                    conventionSlug,
                    conventionProductId: product.id,
                    conventionName: product.name,
                    registrationOptionId: optionId,
                    registrationOptionName: optionName,
                    registrantsCount: isRetreatConvention
                      ? 1
                      : selectedRegistrants || 1,
                  })
                }
              />
            )}

            <div className="pt-4">
              {shouldOfferWaitlist ? (
                <Button
                  type="button"
                  color="green"
                  size="lg"
                  className="w-full text-lg py-4 font-bold"
                  onClick={() => {
                    if (!product?.id) return
                    const queryParams = new URLSearchParams({
                      conventionProductId: product.id.toString(),
                    })
                    if (selectedRegistrationOptionId) {
                      queryParams.set(
                        'registrationOptionId',
                        selectedRegistrationOptionId.toString()
                      )
                    }
                    router.push(
                      `/magic-conventions/${conventionSlug}/register/waitlist?${queryParams.toString()}`
                    )
                  }}
                >
                  Join the Waiting List →
                </Button>
              ) : (
                <Button
                  type="submit"
                  color="green"
                  size="lg"
                  className="w-full text-lg py-4 font-bold"
                >
                  Continue to Registration Details →
                </Button>
              )}
            </div>
          </form>
        </div>
      </ContentWrapper>
    </div>
  )
}
